// See https://github.com/microsoft/TypeScript/issues/38646#issuecomment-1181120676
type PickKeysByValueType_<T, U> = {
  [P in keyof T]: T[P] extends U ? P : never
}[keyof T]

/**
 * Extracts keys from an object `T` which value types extend `U`
 *
 * @example
 * type T = { a: string, b: string, c: boolean }
 * type Ex1 = PickKeysByValueType<T, string>  // 'a' | 'b'
 * type Ex2 = PickKeysByValueType<T, boolean> // 'c'
 * type Ex3 = PickKeysByValueType<T, number>  // never
 */
export type PickKeysByValueType<T, U> = Exclude<
  PickKeysByValueType_<T, U>,
  undefined
>

export class TypeUtil {
  static isTruthy<T>(x: T | undefined | null | false): x is T {
    return !!x
  }
}

export const isAnyObject = (x: unknown): x is Record<string, unknown> =>
  typeof x === 'object' && x !== null

export const isAnyArray = (x: unknown): x is any[] => Array.isArray(x)

export const isAnyString = (x: unknown): x is string => typeof x === 'string'

export const isLimitedString =
  (maxLen: number) =>
  (x: unknown): x is string =>
    typeof x === 'string' && !!x && x.length < maxLen

export const isNotEmptyString = (x: unknown): x is string =>
  typeof x === 'string' && x.length > 0

export const isOptionalString = (x: unknown): x is undefined | string =>
  typeof x === 'undefined' || isNotEmptyString(x)

export const isArrayOf =
  <T>(pred: (x: unknown) => x is T) =>
  (x: unknown): x is T[] =>
    isAnyArray(x) && x.every(pred)

export const maybeUndefinedOrNull =
  <T>(isType: (obj: unknown) => obj is T) =>
  (obj: unknown): obj is T | undefined => {
    return obj === undefined || isType(obj) || obj === null
  }

export const isInteger = (n: unknown): n is number => {
  return Number.isSafeInteger(n)
}

export const isBoolean = (b: unknown): b is boolean => typeof b === 'boolean'

export const isUrl = (url: unknown): url is string => {
  if (!isNotEmptyString(url)) {
    return false
  }

  try {
    return Boolean(new URL(url))
  } catch (e) {
    return false
  }
}
